import { useMemo, useState, useCallback } from 'react'
import { sendEmailVerification } from 'firebase/auth'
import { useAuth } from '../contexts/AuthContext'
import { auth } from '../firebase'
import './EmailVerificationGraceBanner.css'

function usesPasswordProvider(user) {
  return (user?.providerData || []).some((p) => p.providerId === 'password')
}

export default function EmailVerificationGraceBanner() {
  const { user, userData, hasFullCloudAccess } = useAuth()
  const [msg, setMsg] = useState('')

  const info = useMemo(() => {
    if (!user || !usesPasswordProvider(user) || user.emailVerified) return null
    const docEnd = userData?.emailVerificationGraceEndsAt
    const docMs = docEnd?.toMillis?.() ?? null
    const metaMs = user.metadata?.creationTime
      ? new Date(user.metadata.creationTime).getTime() + 7 * 86400000
      : null
    const endMs = docMs ?? metaMs
    const graceActive = endMs != null && endMs > Date.now()
    const daysRemaining = graceActive ? Math.max(1, Math.ceil((endMs - Date.now()) / 86400000)) : 0
    const cloudDuringGrace = hasFullCloudAccess && graceActive
    return { graceActive, daysRemaining, cloudDuringGrace }
  }, [user, userData, hasFullCloudAccess])

  const onResend = useCallback(async () => {
    const u = auth.currentUser
    if (!u) return
    setMsg('')
    try {
      await sendEmailVerification(u)
      setMsg('E-mail de vérification envoyé.')
    } catch (e) {
      setMsg(e?.message || 'Envoi impossible.')
    }
  }, [])

  if (!info) return null

  let text =
    'Confirmez votre adresse e-mail pour sécuriser le compte et éviter une coupure d’accès cloud après la période de grâce.'
  if (info.graceActive && info.cloudDuringGrace) {
    text = `Période de grâce : environ ${info.daysRemaining} jour${info.daysRemaining > 1 ? 's' : ''} restant${info.daysRemaining > 1 ? 's' : ''} pour confirmer votre e-mail. L’accès cloud reste actif tant que vous êtes éligible (beta).`
  } else if (info.graceActive && !info.cloudDuringGrace) {
    text =
      'Confirmez votre e-mail. Sans accès beta complet, le cloud reste limité comme en mode aperçu jusqu’à vérification.'
  }

  let reminder = null
  if (user?.metadata?.creationTime) {
    const dayIdx = Math.floor(
      (Date.now() - new Date(user.metadata.creationTime).getTime()) / 86400000
    )
    if (dayIdx === 0 || dayIdx === 2 || dayIdx === 5) {
      reminder =
        'Pensez à vérifier les courriers indésirables ; certaines messageries institutionnelles bloquent les e-mails Firebase.'
    }
  }

  return (
    <div className="email-verify-grace-banner" role="status">
      <p className="email-verify-grace-banner__text">{text}</p>
      {reminder ? <p className="email-verify-grace-banner__reminder">{reminder}</p> : null}
      <div className="email-verify-grace-banner__actions">
        <button type="button" className="email-verify-grace-banner__btn" onClick={onResend}>
          Renvoyer l’e-mail
        </button>
      </div>
      {msg ? <p className="email-verify-grace-banner__msg">{msg}</p> : null}
    </div>
  )
}
